import React, { useState } from 'react'
import { useSetupWizard } from '../../../contexts/SetupWizardContext'
import { useThemeColors } from '../../../hooks/useThemeColors'
import { PlusIcon, TrashIcon, ClockIcon, CurrencyDollarIcon, UserGroupIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline'

// Service templates based on business type
const serviceTemplates = {
  ice_rink: [
    { 
      name: 'Public Skate', 
      duration: 120, 
      price: 15, 
      description: 'Open skating session',
      category: 'class',
      maxParticipants: 100,
      memberPrice: 12,
      nonMemberPrice: 15
    },
    { 
      name: 'Hockey Practice (1 hour)', 
      duration: 60, 
      price: 200, 
      description: 'Ice rental for team practice',
      category: 'rental',
      isHourlyRental: true,
      peakHourlyRate: 250,
      offPeakHourlyRate: 200,
      minRentalDuration: 60
    },
    { 
      name: 'Learn to Skate', 
      duration: 30, 
      price: 25, 
      description: 'Group skating lessons',
      category: 'class',
      maxParticipants: 20,
      memberPrice: 20,
      nonMemberPrice: 25,
      ageMin: 4,
      ageMax: 12
    },
    { 
      name: 'Private Lesson', 
      duration: 30, 
      price: 75, 
      description: 'One-on-one instruction',
      category: 'service',
      maxParticipants: 1
    },
    { 
      name: 'Birthday Party', 
      duration: 120, 
      price: 299, 
      description: 'Party package with ice time',
      category: 'package',
      maxParticipants: 25,
      includesRental: true
    }
  ],
  training_center: [
    { 
      name: 'Personal Training (1 hour)', 
      duration: 60, 
      price: 85, 
      description: 'One-on-one training session',
      category: 'service',
      maxParticipants: 1
    },
    { 
      name: 'Group Fitness Class', 
      duration: 60, 
      price: 25, 
      description: 'Group workout session',
      category: 'class',
      maxParticipants: 25,
      memberPrice: 20,
      nonMemberPrice: 25
    },
    { 
      name: 'Semi-Private Training', 
      duration: 60, 
      price: 60, 
      description: '2-3 person training',
      category: 'service',
      maxParticipants: 3
    },
    { 
      name: 'Studio Rental', 
      duration: 60, 
      price: 75, 
      description: 'Private studio rental',
      category: 'rental',
      isHourlyRental: true,
      peakHourlyRate: 100,
      offPeakHourlyRate: 75,
      minRentalDuration: 60
    }
  ],
  sports_complex: [
    { 
      name: 'Field Rental (1 hour)', 
      duration: 60, 
      price: 150, 
      description: 'Full field rental',
      category: 'rental',
      isHourlyRental: true,
      peakHourlyRate: 200,
      offPeakHourlyRate: 150,
      minRentalDuration: 60
    },
    { 
      name: 'Court Rental (1 hour)', 
      duration: 60, 
      price: 80, 
      description: 'Indoor court rental',
      category: 'rental',
      isHourlyRental: true,
      peakHourlyRate: 100,
      offPeakHourlyRate: 80,
      minRentalDuration: 60
    },
    { 
      name: 'League Registration', 
      duration: 0, 
      price: 599, 
      description: 'Full season registration',
      category: 'package'
    },
    { 
      name: 'Drop-in Game', 
      duration: 120, 
      price: 20, 
      description: 'Pick-up game session',
      category: 'class',
      maxParticipants: 40,
      memberPrice: 15,
      nonMemberPrice: 20
    }
  ],
  dance_martial_arts: [
    { 
      name: 'Drop-in Class', 
      duration: 60, 
      price: 25, 
      description: 'Single class attendance',
      category: 'class',
      maxParticipants: 30,
      memberPrice: 20,
      nonMemberPrice: 25
    },
    { 
      name: 'Private Lesson', 
      duration: 60, 
      price: 95, 
      description: 'One-on-one instruction',
      category: 'service',
      maxParticipants: 1
    },
    { 
      name: 'Studio Rental', 
      duration: 60, 
      price: 50, 
      description: 'Dance/training studio rental',
      category: 'rental',
      isHourlyRental: true,
      peakHourlyRate: 75,
      offPeakHourlyRate: 50,
      minRentalDuration: 60
    }
  ],
  other: []
}

const serviceCategories = [
  { value: 'class', label: 'Class/Program', icon: UserGroupIcon },
  { value: 'rental', label: 'Facility Rental', icon: BuildingOfficeIcon },
  { value: 'service', label: 'Service', icon: ClockIcon },
  { value: 'package', label: 'Package', icon: CurrencyDollarIcon }
]

export default function ServicesStep() {
  const { wizardData, updateStepData, getStepErrors } = useSetupWizard()
  const colors = useThemeColors()
  const { services, organizationProfile } = wizardData
  const errors = getStepErrors(4)
  const [activeTab, setActiveTab] = useState<'all' | 'class' | 'rental' | 'service' | 'package'>('all')

  const addService = (category: string = 'class') => {
    const newService = {
      id: `service-${Date.now()}`,
      name: '',
      duration: 60,
      price: 0,
      description: '',
      category,
      maxParticipants: category === 'class' ? 20 : 1,
      // Rental specific fields
      isHourlyRental: category === 'rental',
      peakHourlyRate: 0,
      offPeakHourlyRate: 0,
      minRentalDuration: 60,
      // Class specific fields
      memberPrice: 0,
      nonMemberPrice: 0,
      ageMin: null,
      ageMax: null,
      skillLevel: null
    }
    updateStepData('services', [...services, newService])
    setActiveTab(category as typeof activeTab)
  }

  const updateService = (id: string, field: string, value: string | number | boolean | null) => {
    const updated = services.map(service =>
      service.id === id ? { ...service, [field]: value } : service
    )
    updateStepData('services', updated)
  }

  const removeService = (id: string) => {
    const updated = services.filter(service => service.id !== id)
    updateStepData('services', updated)
  }

  const loadTemplates = () => {
    const templates = serviceTemplates[organizationProfile.businessType] || []
    const newServices = templates.map((template, index) => ({
      id: `service-${Date.now()}-${index}`,
      ...template
    }))
    updateStepData('services', newServices)
  }

  const formatDuration = (minutes: number) => {
    if (minutes === 0) return 'N/A'
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours > 0 && mins > 0) return `${hours}h ${mins}m`
    if (hours > 0) return `${hours}h`
    return `${mins}m`
  }

  const filteredServices = activeTab === 'all' 
    ? services 
    : services.filter(s => s.category === activeTab)

  const getCategoryIcon = (category: string) => {
    const cat = serviceCategories.find(c => c.value === category)
    return cat?.icon || ClockIcon
  }

  return (
    <div className="services-step">
      <h2 style={{
        fontSize: '1.75rem',
        fontWeight: '600',
        marginBottom: '0.5rem',
        color: colors.text
      }}>
        Services & Pricing
      </h2>
      <p style={{
        fontSize: '1rem',
        color: colors.textSecondary,
        marginBottom: '2rem'
      }}>
        Configure your classes, rentals, and services with appropriate pricing.
      </p>

      {/* Errors */}
      {errors.length > 0 && (
        <div style={{
          padding: '1rem',
          backgroundColor: `${colors.error}20`,
          border: `1px solid ${colors.error}40`,
          borderRadius: '8px',
          marginBottom: '1.5rem'
        }}>
          <p style={{
            fontSize: '0.875rem',
            color: colors.error,
            margin: 0
          }}>
            {errors[0]}
          </p>
        </div>
      )}

      {/* Template Suggestion */}
      {services.length === 0 && organizationProfile.businessType !== 'other' && (
        <div style={{
          padding: '1rem',
          backgroundColor: `${colors.primary}10`,
          border: `1px solid ${colors.primary}30`,
          borderRadius: '8px',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <p style={{
            fontSize: '0.875rem',
            color: colors.text,
            margin: 0
          }}>
            💡 Start with common services for {organizationProfile.businessType.replace(/_/g, ' ')}?
          </p>
          <button
            onClick={loadTemplates}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: colors.primary,
              color: colors.background,
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.813rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Use Templates
          </button>
        </div>
      )}

      {/* Category Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        borderBottom: `1px solid ${colors.border}`,
        overflowX: 'auto'
      }}>
        <button
          onClick={() => setActiveTab('all')}
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'all' ? `2px solid ${colors.primary}` : '2px solid transparent',
            color: activeTab === 'all' ? colors.primary : colors.textSecondary,
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          All ({services.length})
        </button>
        {serviceCategories.map(cat => {
          const count = services.filter(s => s.category === cat.value).length
          return (
            <button
              key={cat.value}
              onClick={() => setActiveTab(cat.value as typeof activeTab)}
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: activeTab === cat.value ? `2px solid ${colors.primary}` : '2px solid transparent',
                color: activeTab === cat.value ? colors.primary : colors.textSecondary,
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <cat.icon style={{ width: '16px', height: '16px' }} />
              {cat.label} ({count})
            </button>
          )
        })}
      </div>

      {/* Services List */}
      <div style={{
        display: 'grid',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {filteredServices.map((service, index) => {
          const Icon = getCategoryIcon(service.category)
          return (
            <div
              key={service.id}
              style={{
                backgroundColor: colors.background,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                padding: '1.5rem'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Icon style={{ 
                    width: '20px', 
                    height: '20px', 
                    color: colors.primary 
                  }} />
                  <h3 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: colors.text,
                    margin: 0
                  }}>
                    {service.name || `New ${service.category}`}
                  </h3>
                </div>
                <button
                  onClick={() => removeService(service.id)}
                  style={{
                    padding: '0.25rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: colors.error,
                    cursor: 'pointer'
                  }}
                >
                  <TrashIcon style={{ width: '20px', height: '20px' }} />
                </button>
              </div>

              <div style={{
                display: 'grid',
                gap: '1rem'
              }}>
                {/* Service Name */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.813rem',
                    fontWeight: '500',
                    marginBottom: '0.25rem',
                    color: colors.textSecondary
                  }}>
                    Name <span style={{ color: colors.error }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={service.name}
                    onChange={(e) => updateService(service.id, 'name', e.target.value)}
                    placeholder={
                      service.category === 'rental' ? 'e.g., Ice Rink Rental' :
                      service.category === 'class' ? 'e.g., Beginner Hockey' :
                      service.category === 'service' ? 'e.g., Private Coaching' :
                      'e.g., Birthday Party Package'
                    }
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      backgroundColor: colors.backgroundLight,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                {/* Category-specific fields */}
                {service.category === 'rental' ? (
                  <>
                    {/* Rental Rate Configuration */}
                    <div style={{
                      backgroundColor: colors.backgroundLight,
                      padding: '1rem',
                      borderRadius: '6px',
                      border: `1px solid ${colors.border}`
                    }}>
                      <h4 style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: colors.text,
                        marginBottom: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <CurrencyDollarIcon style={{ width: '16px', height: '16px' }} />
                        Rental Rates
                      </h4>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            marginBottom: '0.25rem',
                            color: colors.textSecondary
                          }}>
                            Peak Rate ($/hour)
                          </label>
                          <input
                            type="number"
                            value={service.peakHourlyRate || 0}
                            onChange={(e) => updateService(service.id, 'peakHourlyRate', parseFloat(e.target.value) || 0)}
                            placeholder="250"
                            min="0"
                            step="5"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              backgroundColor: colors.background,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              color: colors.text,
                              fontSize: '0.875rem'
                            }}
                          />
                          <p style={{
                            fontSize: '0.688rem',
                            color: colors.textMuted,
                            marginTop: '0.125rem'
                          }}>
                            Weekends & evenings
                          </p>
                        </div>

                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            marginBottom: '0.25rem',
                            color: colors.textSecondary
                          }}>
                            Off-Peak Rate ($/hour)
                          </label>
                          <input
                            type="number"
                            value={service.offPeakHourlyRate || 0}
                            onChange={(e) => updateService(service.id, 'offPeakHourlyRate', parseFloat(e.target.value) || 0)}
                            placeholder="150"
                            min="0"
                            step="5"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              backgroundColor: colors.background,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              color: colors.text,
                              fontSize: '0.875rem'
                            }}
                          />
                          <p style={{
                            fontSize: '0.688rem',
                            color: colors.textMuted,
                            marginTop: '0.125rem'
                          }}>
                            Weekday daytime
                          </p>
                        </div>
                      </div>

                      <div style={{ marginTop: '1rem' }}>
                        <label style={{
                          display: 'block',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          marginBottom: '0.25rem',
                          color: colors.textSecondary
                        }}>
                          Minimum Rental Duration
                        </label>
                        <select
                          value={service.minRentalDuration || 60}
                          onChange={(e) => updateService(service.id, 'minRentalDuration', parseInt(e.target.value))}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            backgroundColor: colors.background,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.875rem'
                          }}
                        >
                          <option value="30">30 minutes</option>
                          <option value="60">1 hour</option>
                          <option value="90">1.5 hours</option>
                          <option value="120">2 hours</option>
                        </select>
                      </div>
                    </div>
                  </>
                ) : service.category === 'class' ? (
                  <>
                    {/* Class Configuration */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.813rem',
                          fontWeight: '500',
                          marginBottom: '0.25rem',
                          color: colors.textSecondary
                        }}>
                          Duration <span style={{ color: colors.error }}>*</span>
                        </label>
                        <input
                          type="number"
                          value={service.duration}
                          onChange={(e) => updateService(service.id, 'duration', parseInt(e.target.value) || 0)}
                          placeholder="60"
                          min="0"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            backgroundColor: colors.backgroundLight,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.875rem'
                          }}
                        />
                        <p style={{
                          fontSize: '0.75rem',
                          color: colors.textMuted,
                          marginTop: '0.25rem'
                        }}>
                          {formatDuration(service.duration)}
                        </p>
                      </div>

                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.813rem',
                          fontWeight: '500',
                          marginBottom: '0.25rem',
                          color: colors.textSecondary
                        }}>
                          Max Participants
                        </label>
                        <input
                          type="number"
                          value={service.maxParticipants || 20}
                          onChange={(e) => updateService(service.id, 'maxParticipants', parseInt(e.target.value) || 1)}
                          placeholder="20"
                          min="1"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            backgroundColor: colors.backgroundLight,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                    </div>

                    {/* Member/Non-Member Pricing */}
                    <div style={{
                      backgroundColor: colors.backgroundLight,
                      padding: '1rem',
                      borderRadius: '6px',
                      border: `1px solid ${colors.border}`
                    }}>
                      <h4 style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: colors.text,
                        marginBottom: '0.75rem'
                      }}>
                        Pricing Tiers
                      </h4>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            marginBottom: '0.25rem',
                            color: colors.textSecondary
                          }}>
                            Member Price ($)
                          </label>
                          <input
                            type="number"
                            value={service.memberPrice || 0}
                            onChange={(e) => updateService(service.id, 'memberPrice', parseFloat(e.target.value) || 0)}
                            placeholder="20"
                            min="0"
                            step="0.01"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              backgroundColor: colors.background,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              color: colors.text,
                              fontSize: '0.875rem'
                            }}
                          />
                        </div>

                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            marginBottom: '0.25rem',
                            color: colors.textSecondary
                          }}>
                            Non-Member Price ($)
                          </label>
                          <input
                            type="number"
                            value={service.nonMemberPrice || service.price || 0}
                            onChange={(e) => updateService(service.id, 'nonMemberPrice', parseFloat(e.target.value) || 0)}
                            placeholder="25"
                            min="0"
                            step="0.01"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              backgroundColor: colors.background,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              color: colors.text,
                              fontSize: '0.875rem'
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Age Restrictions (Optional) */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.813rem',
                          fontWeight: '500',
                          marginBottom: '0.25rem',
                          color: colors.textSecondary
                        }}>
                          Min Age (optional)
                        </label>
                        <input
                          type="number"
                          value={service.ageMin || ''}
                          onChange={(e) => updateService(service.id, 'ageMin', e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="No minimum"
                          min="0"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            backgroundColor: colors.backgroundLight,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.813rem',
                          fontWeight: '500',
                          marginBottom: '0.25rem',
                          color: colors.textSecondary
                        }}>
                          Max Age (optional)
                        </label>
                        <input
                          type="number"
                          value={service.ageMax || ''}
                          onChange={(e) => updateService(service.id, 'ageMax', e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="No maximum"
                          min="0"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            backgroundColor: colors.backgroundLight,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Standard Service/Package */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.813rem',
                          fontWeight: '500',
                          marginBottom: '0.25rem',
                          color: colors.textSecondary
                        }}>
                          Duration (minutes) <span style={{ color: colors.error }}>*</span>
                        </label>
                        <input
                          type="number"
                          value={service.duration}
                          onChange={(e) => updateService(service.id, 'duration', parseInt(e.target.value) || 0)}
                          placeholder="60"
                          min="0"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            backgroundColor: colors.backgroundLight,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.875rem'
                          }}
                        />
                        <p style={{
                          fontSize: '0.75rem',
                          color: colors.textMuted,
                          marginTop: '0.25rem'
                        }}>
                          {formatDuration(service.duration)}
                        </p>
                      </div>

                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.813rem',
                          fontWeight: '500',
                          marginBottom: '0.25rem',
                          color: colors.textSecondary
                        }}>
                          Price ($) <span style={{ color: colors.error }}>*</span>
                        </label>
                        <input
                          type="number"
                          value={service.price}
                          onChange={(e) => updateService(service.id, 'price', parseFloat(e.target.value) || 0)}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            backgroundColor: colors.backgroundLight,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Description */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.813rem',
                    fontWeight: '500',
                    marginBottom: '0.25rem',
                    color: colors.textSecondary
                  }}>
                    Description
                  </label>
                  <textarea
                    value={service.description || ''}
                    onChange={(e) => updateService(service.id, 'description', e.target.value)}
                    placeholder="Brief description of this service"
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      backgroundColor: colors.backgroundLight,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      fontSize: '0.875rem',
                      resize: 'vertical',
                      minHeight: '60px'
                    }}
                  />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Add Service Buttons */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '0.75rem'
      }}>
        {serviceCategories.map(cat => (
          <button
            key={cat.value}
            onClick={() => addService(cat.value)}
            style={{
              padding: '1rem',
              backgroundColor: 'transparent',
              border: `2px dashed ${colors.border}`,
              borderRadius: '8px',
              color: colors.primary,
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = colors.primary
              e.currentTarget.style.backgroundColor = `${colors.primary}10`
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.border
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            <PlusIcon style={{ width: '20px', height: '20px' }} />
            Add {cat.label}
          </button>
        ))}
      </div>
    </div>
  )
}